import type { StandardServicePageContent } from "~/content/servicePage";

export const GasTightnessTestContent: StandardServicePageContent = {
  heroTitle: "Próba szczelności gazu",
  quickFactsTitle: "Najważniejsze informacje w skrócie",
  quickFacts: [
    {
      label: "Kiedy potrzebna",
      value: "Po nowej instalacji, naprawie, przebudowie lub przed uruchomieniem",
      description:
        "To badanie typowe dla momentu odbioru i ponownego dopuszczenia instalacji do użytkowania. Nie zastępuje corocznego przeglądu gazowego.",
    },
    {
      label: "Co potwierdza",
      value: "Czy badany odcinek instalacji jest szczelny",
      description:
        "Celem próby jest jednoznaczne sprawdzenie przewodów, połączeń i armatury. Jeśli wynik jest negatywny, potrzebna jest dalsza lokalizacja nieszczelności.",
    },
    {
      label: "Jaki dokument",
      value: "Protokół z wynikiem próby szczelności",
      description:
        "Dokument jest potrzebny do odbioru instalacji, dokumentacji technicznej i dalszego uruchomienia gazu po wykonanych pracach.",
    },
    {
      label: "Ile kosztuje",
      value: "Cena zależy od wielkości instalacji i liczby odcinków",
      description:
        "Proste badanie w mieszkaniu wycenia się inaczej niż próbę dla domu, lokalu usługowego albo instalacji po większej przebudowie. Znaczenie ma też to, czy potrzebna jest diagnostyka po negatywnym wyniku.",
    },
  ],
  faqTitle: "Najczęstsze pytania o próbę szczelności gazu",
  sections: [
    {
      title: "Na czym polega próba szczelności instalacji gazowej",
      paragraphs: [
        `Próba szczelności instalacji gazowej polega na sprawdzeniu, czy cały badany odcinek instalacji jest szczelny i może zostać bezpiecznie uruchomiony albo przekazany do dalszego odbioru. To badanie techniczne wykonywane po to, by potwierdzić brak nieszczelności na przewodach, połączeniach i armaturze, a nie ogólna kontrola eksploatacyjna całego budynku.`,
        `W praktyce próba szczelności jest potrzebna wtedy, gdy trzeba jednoznacznie potwierdzić stan instalacji po wykonaniu nowych prac albo po ingerencji w istniejący układ. Jej celem jest wychwycenie błędów montażowych i ryzyka wycieku jeszcze zanim gaz zostanie ponownie dopuszczony do instalacji.`,
        `Dobrze wykonane badanie daje inwestorowi, właścicielowi albo zarządcy jasną odpowiedź, czy instalacja spełnia warunki do dalszego uruchomienia, czy najpierw trzeba usunąć usterki i powtórzyć test.`,
      ],
    },
    {
      title: "Kiedy wykonuje się próbę szczelności gazu i czy jest obowiązkowa",
      paragraphs: [
        `Próbę szczelności gazu wykonuje się najczęściej po wykonaniu nowej instalacji, po przebudowie, po wymianie fragmentu przewodów, po naprawie nieszczelności albo przed ponownym uruchomieniem instalacji, która przez dłuższy czas nie była użytkowana. To badanie typowe dla momentów odbioru, uruchomienia i przekazania instalacji do eksploatacji.`,
        `W praktyce warto zlecić próbę jeszcze przed finalnym odbiorem robót lub wizytą kolejnego wykonawcy. Dzięki temu ewentualne poprawki można wykonać bez zatrzymywania całego procesu inwestycyjnego i bez ryzyka, że problem wyjdzie dopiero na końcu prac.`,
        `Jeśli po remoncie albo naprawie są wątpliwości co do stanu instalacji, próba szczelności jest właściwym krokiem potwierdzającym bezpieczeństwo konkretnego odcinka prac. Nie zastępuje rocznego przeglądu gazowego, ale odpowiada na inne pytanie: czy układ jest szczelny tu i teraz.`,
      ],
    },
    {
      title: "Jak wygląda badanie i kiedy potrzebne jest wykrywanie nieszczelności gazu",
      paragraphs: [
        `Wykrywanie nieszczelności gazu to etap diagnostyczny potrzebny wtedy, gdy próba szczelności albo bieżąca kontrola wskazuje, że gdzieś w układzie występuje problem. W takiej sytuacji celem nie jest już samo potwierdzenie, że instalacja jest nieszczelna, ale precyzyjna lokalizacja wycieku gazu i wskazanie miejsca wymagającego naprawy.`,
        `Do takiej pracy wykorzystuje się detekcję gazu, testy punktowe i ocenę newralgicznych połączeń. Dobrze przeprowadzona lokalizacja wycieku pozwala zawęzić zakres naprawy, uniknąć niepotrzebnego demontażu większych fragmentów instalacji i szybciej przygotować układ do ponownego badania.`,
        `Badanie szczelności zaczyna się od przygotowania układu do testu. Technik odcina instalację od urządzeń albo dzieli ją na odpowiednie odcinki, sprawdza warunki wykonania badania i dopiero wtedy przechodzi do właściwego pomiaru. Jeśli wynik jest negatywny, potrzebna jest lokalizacja wycieku, naprawa wskazanych miejsc i ponowne wykonanie próby.`,
      ],
    },
    {
      title: "Protokół z próby szczelności gazu i cena usługi",
      paragraphs: [
        `Po wykonaniu badania właściciel lub inwestor powinien otrzymać protokół z próby szczelności. Dokument potwierdza zakres badania, wynik i podstawowe dane instalacji, a przy odbiorze robót często jest jednym z kluczowych załączników porządkujących dokumentację techniczną.`,
        `Cena próby szczelności gazu zależy od wielkości instalacji, liczby badanych odcinków, złożoności układu i celu wizyty. Inaczej wycenia się prostą próbę dla mieszkania lub domu, a inaczej badanie większej instalacji w budynku wielorodzinnym, lokalu usługowym albo obiekcie po szerszej modernizacji.`,
        `Na koszt wpływa też to, czy usługa obejmuje wyłącznie samo badanie, czy również diagnostykę po negatywnym wyniku, lokalizację nieszczelności i przygotowanie pełnej dokumentacji. To zakres odpowiedzialności za wynik usługi decyduje o jej realnej wartości.`,
      ],
    },
  ],
  scopeTitle: "Zakres usług próby szczelności gazu",
  scopeItems: [
    "Przygotowanie instalacji do próby",
    "Badanie szczelności odcinka instalacji",
    "Kontrola połączeń i armatury",
    "Ocena wyniku próby",
    "Detekcja nieszczelności po wyniku negatywnym",
    "Lokalizacja wycieku gazu",
    "Próba do odbioru instalacji",
    "Protokół z badania",
  ],
  seo: {
    title: "Próba szczelności gazu: kiedy jest obowiązkowa, protokół i cena",
    description:
      "Zamów próbę szczelności gazu dla nowej, naprawianej lub przebudowanej instalacji. Sprawdź, kiedy wykonuje się badanie, co potwierdza protokół i co wpływa na cenę usługi.",
  },
};
